import React, { useState, useEffect } from "react";
import ChartistGraph from "react-chartist";
import TransactionRow from "components/TransactionRow";
import { useSupabase } from '../context/SupabaseContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import jsPDF from 'jspdf';
var moment = require('moment');

// react-bootstrap components
import {
  Card,
  Table,
  Container,
  Row,
  Col,
  Badge,
} from "react-bootstrap";

// Generate real AI insights based on data
const generateAIInsights = (transactions, subscriptions, profile, goals) => {
  const insights = [];
  
  // Calculate spending by category
  const spendingByCategory = {};
  transactions?.forEach(transaction => {
    const category = transaction.category || 'General';
    spendingByCategory[category] = (spendingByCategory[category] || 0) + parseFloat(transaction.amount || 0);
  });
  
  // Find highest spending category
  const highestCategory = Object.entries(spendingByCategory).reduce((a, b) => a[1] > b[1] ? a : b, ['', 0]);
  
  // Calculate total subscriptions
  const totalSubscriptions = subscriptions?.reduce((sum, s) => sum + parseFloat(s.amount || 0), 0) || 0;
  
  // Calculate savings rate
  const totalIncome = profile?.monthly_income || 0;
  const totalExpenses = transactions?.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0) || 0;
  const savingsRate = totalIncome > 0 ? ((totalIncome - totalExpenses - totalSubscriptions) / totalIncome * 100) : 0;
  
  // Generate insights
  if (highestCategory[1] > 0) {
    insights.push({
      id: 1,
      type: 'warning',
      text: `Your highest spending category is ${highestCategory[0]} at $${highestCategory[1].toFixed(2)} this month`
    });
  }
  
  if (totalSubscriptions > 100) {
    insights.push({
      id: 2,
      type: 'success',
      text: `You can save $${totalSubscriptions.toFixed(2)} by reviewing your ${subscriptions?.length || 0} subscription services`
    });
  }
  
  if (savingsRate < 20 && savingsRate > 0) {
    insights.push({
      id: 3,
      type: 'info',
      text: `Your savings rate is ${savingsRate.toFixed(1)}% - consider increasing to 20% for better financial health`
    });
  }
  
  if (goals && goals.length > 0) {
    const completedGoals = goals.filter(g => g.completed).length;
    insights.push({
      id: 4,
      type: 'success',
      text: `You've completed ${completedGoals} out of ${goals.length} financial goals - keep going!`
    });
  }
  
  if (totalExpenses > totalIncome * 0.8) {
    insights.push({
      id: 5,
      type: 'warning',
      text: `You've spent ${((totalExpenses / totalIncome) * 100).toFixed(1)}% of your monthly income - consider reducing expenses`
    });
  }
  
  return insights;
};

function PremiumDashboard() {
  console.log('PremiumDashboard component loading...');
  
  const notifySuccess = (message) => toast.success(message, {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });
  
  const notifyFailure = () => toast.error("An Error Occurred", {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });

  const { 
    profile, 
    transactions, 
    subscriptions, 
    goals, 
    totalIncome, 
    totalExpenses, 
    totalSubscriptions, 
    balance,
    removeTransaction 
  } = useSupabase();
  
  console.log('Supabase data loaded:', { profile, transactions: transactions?.length, subscriptions: subscriptions?.length, goals: goals?.length });

  const calculateTotalSpent = () => {
    return transactions?.reduce((total, transaction) => total + (parseFloat(transaction.amount) || 0), 0) || 0;
  };

  const getCategories = () => {
    const categories = {};
    transactions?.forEach(transaction => {
      let category = transaction.category || 'Uncategorized';
      
      // Fix common spelling/capitalization issues
      if (category.toLowerCase() === 'electronics' || category.toLowerCase() === 'lectronics') {
        category = 'Electronics';
      }
      
      console.log('Fixed transaction category:', category);
      
      if (!categories[category]) {
        categories[category] = { name: category, totalAmount: 0 };
      }
      categories[category].totalAmount += parseFloat(transaction.amount) || 0;
    });
    
    const categoryArray = Object.values(categories);
    console.log('All categories before processing:', categoryArray);
    
    // Ensure minimum data for chart rendering
    if (categoryArray.length === 0) {
      return [{ name: 'No Data', totalAmount: 0 }];
    }
    
    // Calculate total spending and add percentage and color based on spending
    const totalSpending = categoryArray.reduce((sum, cat) => sum + cat.totalAmount, 0);
    
    // Sort categories by amount (highest first) to ensure proper color assignment
    const sortedCategories = categoryArray
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .map((category, index) => {
        const percentage = (category.totalAmount / totalSpending) * 100;
        let color;
        
        // Assign colors based on sorted order (highest spending first)
        if (index === 0) {
          color = '#DC2626'; // Red for highest spending
        } else if (index === 1) {
          color = '#F59E0B'; // Orange for second highest
        } else if (index === 2) {
          color = '#FBBF24'; // Yellow for third highest
        } else if (index === 3) {
          color = '#10B981'; // Green for fourth highest
        } else {
          color = '#3B82F6'; // Blue for all others
        }
        
        console.log('Category:', category.name, 'Index:', index, 'Color:', color, 'Percentage:', percentage.toFixed(1));
        
        return {
          ...category,
          percentage: percentage.toFixed(1),
          color: color
        };
      });
    
    console.log('Final sorted categories:', sortedCategories);
    return sortedCategories;
  };

  const totalBalance = totalIncome - calculateTotalSpent();
  const savingsRate = totalIncome > 0 ? ((totalIncome - calculateTotalSpent()) / totalIncome * 100).toFixed(1) : 0;

  const exportReport = (format) => {
    console.log('Export report called with format:', format);
    if (format === 'csv') {
      console.log('Calling exportCSV');
      exportCSV();
    } else if (format === 'pdf') {
      console.log('Calling exportPDF');
      exportPDF();
    }
  };

  const exportCSV = () => {
    console.log('Starting CSV export...');
    // Generate CSV content
    const csvContent = [];
    
    // Add header
    csvContent.push('Financial Report');
    csvContent.push(`Generated: ${new Date().toLocaleString()}`);
    csvContent.push('');
    
    // Profile Summary
    csvContent.push('PROFILE SUMMARY');
    csvContent.push('Name,Email,Monthly Income,Salary Date');
    csvContent.push(`${profile?.name || 'N/A'},${profile?.email || 'N/A'},$${profile?.monthly_income || 0},${profile?.salary_date || 'N/A'}`);
    csvContent.push('');
    
    // Transactions
    csvContent.push('TRANSACTIONS');
    csvContent.push('Date,Name,Category,Amount,Payment Mode,Type');
    transactions?.forEach(transaction => {
      csvContent.push(`${transaction.date},${transaction.name},${transaction.category || 'General'},$${transaction.amount},${transaction.paymentMode || 'N/A'},${transaction.type || 'expense'}`);
    });
    csvContent.push('');
    
    // Subscriptions
    csvContent.push('SUBSCRIPTIONS');
    csvContent.push('Name,Amount,Billing Cycle,Start Date');
    subscriptions?.forEach(subscription => {
      csvContent.push(`${subscription.name},$${subscription.amount},${subscription.billing_cycle || 'monthly'},${subscription.start_date}`);
    });
    csvContent.push('');
    
    // Goals
    csvContent.push('FINANCIAL GOALS');
    csvContent.push('Name,Target Amount,Current Amount,Description,Target Date,Status');
    goals?.forEach(goal => {
      csvContent.push(`${goal.name},$${goal.target_amount},$${goal.current_amount},${goal.description || 'N/A'},${goal.target_date || 'N/A'},${goal.completed ? 'Completed' : 'In Progress'}`);
    });
    csvContent.push('');
    
    // Summary Statistics
    csvContent.push('SUMMARY STATISTICS');
    csvContent.push('Metric,Value');
    csvContent.push(`Total Income,$${totalIncome}`);
    csvContent.push(`Total Expenses,$${calculateTotalSpent()}`);
    csvContent.push(`Total Subscriptions,$${totalSubscriptions}`);
    csvContent.push(`Net Balance,$${totalBalance}`);
    csvContent.push(`Savings Rate,${savingsRate}%`);
    csvContent.push(`Total Transactions,${transactions?.length || 0}`);
    csvContent.push(`Active Subscriptions,${subscriptions?.length || 0}`);
    csvContent.push(`Total Goals,${goals?.length || 0}`);
    csvContent.push(`Completed Goals,${goals?.filter(g => g.completed).length || 0}`);
    
    // Convert to CSV string
    const csvString = csvContent.join('\n');
    
    // Create blob and download
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `financial_report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    notifySuccess('Financial CSV report exported successfully!');
  };

  const exportPDF = () => {
    console.log('Starting enhanced PDF export...');
    const doc = new jsPDF();
    
    // Define color scheme
    const colors = {
      primary: [59, 130, 246],      // Blue
      secondary: [16, 185, 129],     // Green
      accent: [245, 158, 11],       // Orange
      dark: [17, 24, 39],           // Dark gray
      light: [243, 244, 246],       // Light gray
      text: [55, 65, 81],           // Text color
      muted: [107, 114, 128]        // Muted text
    };
    
    // Add professional header with gradient effect
    doc.setFillColor(...colors.primary);
    doc.rect(0, 0, 210, 50, 'F');
    
    // Add logo placeholder circle
    doc.setFillColor(255, 255, 255);
    doc.circle(25, 25, 8, 'F');
    doc.setFillColor(...colors.primary);
    doc.circle(25, 25, 6, 'F');
    
    // Add company name and tagline
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.setFont(undefined, 'bold');
    doc.text('FINOVA', 45, 30);
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text('Personal Finance Dashboard', 45, 38);
    
    doc.setFontSize(9);
    doc.setTextColor(200, 200, 200);
    doc.text('Financial Management System', 45, 44);
    
    // Add report date on the right
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text(`Report Date: ${new Date().toLocaleDateString()}`, 190, 30, { align: 'right' });
    doc.text(`Generated: ${new Date().toLocaleTimeString()}`, 190, 38, { align: 'right' });
    
    let yPosition = 65;
    
    // Executive Summary Box
    doc.setFillColor(...colors.light);
    doc.roundedRect(15, yPosition - 10, 180, 35, 3, 3, 'F');
    doc.setDrawColor(...colors.primary);
    doc.roundedRect(15, yPosition - 10, 180, 35, 3, 3, 'S');
    
    doc.setTextColor(...colors.dark);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Executive Summary', 25, yPosition);
    
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...colors.text);
    doc.text(`Net Balance: $${totalBalance}`, 25, yPosition + 8);
    doc.text(`Savings Rate: ${savingsRate}%`, 25, yPosition + 16);
    
    // Add visual indicators
    const balanceColor = totalBalance >= 0 ? colors.secondary : colors.accent;
    doc.setFillColor(...balanceColor);
    doc.circle(120, yPosition + 5, 3, 'F');
    doc.setTextColor(...colors.text);
    doc.text(totalBalance >= 0 ? 'Positive' : 'Negative', 130, yPosition + 8);
    
    const savingsColor = savingsRate >= 20 ? colors.secondary : savingsRate >= 10 ? colors.accent : colors.muted;
    doc.setFillColor(...savingsColor);
    doc.circle(120, yPosition + 13, 3, 'F');
    doc.setTextColor(...colors.text);
    doc.text(savingsRate >= 20 ? 'Excellent' : savingsRate >= 10 ? 'Good' : 'Needs Improvement', 130, yPosition + 16);
    
    yPosition += 40;
    
    // Profile Information Section
    doc.setTextColor(...colors.dark);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Profile Information', 20, yPosition);
    
    yPosition += 8;
    doc.setDrawColor(...colors.muted);
    doc.setLineWidth(0.5);
    doc.line(20, yPosition, 190, yPosition);
    yPosition += 10;
    
    // Profile details in two columns
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...colors.text);
    
    const profileData = [
      ['Name:', profile?.name || 'N/A'],
      ['Email:', profile?.email || 'N/A'],
      ['Monthly Income:', `$${profile?.monthly_income || 0}`],
      ['Salary Date:', profile?.salary_date || 'N/A']
    ];
    
    profileData.forEach(([label, value], index) => {
      const xPos = index % 2 === 0 ? 25 : 105;
      const yPos = yPosition + Math.floor(index / 2) * 8;
      
      doc.setTextColor(...colors.muted);
      doc.setFont(undefined, 'normal');
      doc.text(label, xPos, yPos);
      
      doc.setTextColor(...colors.text);
      doc.setFont(undefined, 'bold');
      doc.text(value, xPos + 35, yPos);
    });
    
    yPosition += Math.ceil(profileData.length / 2) * 8 + 15;
    
    // Financial Overview Table
    doc.setTextColor(...colors.dark);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Financial Overview', 20, yPosition);
    
    yPosition += 8;
    doc.setDrawColor(...colors.muted);
    doc.line(20, yPosition, 190, yPosition);
    yPosition += 10;
    
    // Create table for financial data
    const financialData = [
      ['Total Income', `$${totalIncome}`, colors.secondary],
      ['Total Expenses', `$${calculateTotalSpent()}`, colors.accent],
      ['Total Subscriptions', `$${totalSubscriptions}`, colors.muted],
      ['Net Balance', `$${totalBalance}`, totalBalance >= 0 ? colors.secondary : colors.accent]
    ];
    
    // Table header
    doc.setFillColor(...colors.light);
    doc.rect(20, yPosition - 5, 170, 10, 'F');
    doc.setTextColor(...colors.dark);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Metric', 25, yPosition);
    doc.text('Amount', 120, yPosition);
    doc.text('Status', 160, yPosition);
    
    yPosition += 10;
    
    // Table rows
    financialData.forEach(([metric, amount, color]) => {
      doc.setDrawColor(...colors.muted);
      doc.setLineWidth(0.2);
      doc.line(20, yPosition, 190, yPosition);
      
      doc.setTextColor(...colors.text);
      doc.setFont(undefined, 'normal');
      doc.text(metric, 25, yPosition + 6);
      
      doc.setFont(undefined, 'bold');
      doc.text(amount, 120, yPosition + 6);
      
      // Status indicator
      doc.setFillColor(...color);
      doc.circle(160, yPosition + 3, 2, 'F');
      
      yPosition += 8;
    });
    
    yPosition += 10;
    
    // Statistics Grid
    doc.setTextColor(...colors.dark);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Key Statistics', 20, yPosition);
    
    yPosition += 8;
    doc.setDrawColor(...colors.muted);
    doc.line(20, yPosition, 190, yPosition);
    yPosition += 15;
    
    // Create 2x2 grid for statistics
    const stats = [
      ['Total Transactions', transactions?.length || 0, colors.primary],
      ['Active Subscriptions', subscriptions?.length || 0, colors.accent],
      ['Total Goals', goals?.length || 0, colors.secondary],
      ['Completed Goals', goals?.filter(g => g.completed).length || 0, colors.muted]
    ];
    
    stats.forEach(([label, value, color], index) => {
      const row = Math.floor(index / 2);
      const col = index % 2;
      const xPos = col === 0 ? 25 : 105;
      const yPos = yPosition + row * 25;
      
      // Box for each stat
      doc.setFillColor(...colors.light);
      doc.roundedRect(xPos - 5, yPos - 8, 75, 20, 2, 2, 'F');
      doc.setDrawColor(...color);
      doc.roundedRect(xPos - 5, yPos - 8, 75, 20, 2, 2, 'S');
      
      // Stat value
      doc.setFillColor(...color);
      doc.circle(xPos, yPos - 3, 2, 'F');
      doc.setTextColor(...colors.dark);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(value.toString(), xPos + 8, yPos);
      
      // Stat label
      doc.setTextColor(...colors.muted);
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.text(label, xPos + 8, yPos + 8);
    });
    
    yPosition += 60;
    
    // Recent Transactions (if space allows)
    if (transactions && transactions.length > 0 && yPosition < 240) {
      doc.setTextColor(...colors.dark);
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('Recent Transactions', 20, yPosition);
      
      yPosition += 8;
      doc.setDrawColor(...colors.muted);
      doc.line(20, yPosition, 190, yPosition);
      yPosition += 10;
      
      // Transaction table
      const headers = ['Date', 'Description', 'Category', 'Amount'];
      const headerWidths = [30, 60, 40, 30];
      
      // Header row
      doc.setFillColor(...colors.light);
      doc.rect(20, yPosition - 5, 170, 8, 'F');
      doc.setTextColor(...colors.dark);
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      
      let currentX = 25;
      headers.forEach((header, index) => {
        doc.text(header, currentX, yPosition);
        currentX += headerWidths[index];
      });
      
      yPosition += 12;
      
      // Transaction rows
      transactions.slice(0, 5).forEach((t) => {
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setDrawColor(...colors.muted);
        doc.setLineWidth(0.1);
        doc.line(20, yPosition - 2, 190, yPosition - 2);
        
        doc.setTextColor(...colors.text);
        doc.setFont(undefined, 'normal');
        
        currentX = 25;
        const rowData = [
          moment(t.date).format('MM/DD'),
          t.name.length > 15 ? t.name.substring(0, 15) + '...' : t.name,
          t.category || 'General',
          `$${t.amount}`
        ];
        
        rowData.forEach((data, index) => {
          doc.text(data, currentX, yPosition);
          currentX += headerWidths[index];
        });
        
        yPosition += 8;
      });
    }
    
        
    // Save the PDF
    doc.save(`finova_financial_report_${new Date().toISOString().split('T')[0]}.pdf`);
    
    notifySuccess('Enhanced financial PDF report exported successfully!');
  };

  const deleteTrans = async (id) => {
    console.log('Attempting to delete transaction with ID:', id);
    
    if (!id) {
      console.error('No transaction ID provided for deletion');
      notifyFailure('Error: No transaction ID');
      return;
    }
    
    try {
      console.log('Calling removeTransaction with ID:', id);
      const result = await removeTransaction(id);
      console.log('Delete result:', result);
      
      if (result.success) {
        notifySuccess("Successfully Deleted")
        console.log('Transaction deleted successfully');
      } else {
        console.error('Delete failed:', result.error);
        notifyFailure(result.error || 'Delete failed')
      }
    } catch (e) {
      console.error('Exception during deletion:', e);
      notifyFailure(e.message || 'Error deleting transaction')
    }
  }

  useEffect(() => {
    // Disabled - using global state instead
    console.log('useEffect disabled - using global state');
  }, []) // Added useEffect to refresh data

  return (
    <>
      <ToastContainer />
      <div className="dashboard">
        <Container fluid>
          {/* Dashboard Header */}
          <div className="dashboard-header">
            <div className="row align-items-center">
              <div className="col">
                <h1 className="dashboard-title">Financial Overview</h1>
                <p className="dashboard-subtitle">Track your finances and make informed decisions</p>
              </div>
              <div className="col-auto">
                <div className="btn-group" role="group">
                  <button className="btn btn-primary" onClick={() => exportReport('csv')}>
                    <i className="fas fa-file-csv mr-2"></i>
                    CSV
                  </button>
                  <button className="btn btn-success" onClick={() => exportReport('pdf')}>
                    <i className="fas fa-file-pdf mr-2"></i>
                    PDF
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* AI Insights Section */}
          <div className="ai-insights fade-in">
            <div className="ai-insights-header">
              <i className="fas fa-brain ai-insights-icon"></i>
              <h5 className="ai-insights-title">AI Financial Insights</h5>
            </div>
            <ul className="ai-insights-list">
              {generateAIInsights(transactions, subscriptions, profile, goals).map((insight) => (
                <li key={insight.id} className={`ai-insights-item ${insight.type}`}>
                  <i className={`fas fa-${insight.type === 'warning' ? 'exclamation-triangle' : insight.type === 'success' ? 'check-circle' : 'info-circle'} ai-insights-icon`}></i>
                  <span className="ai-insights-text">{insight.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Stats Grid */}
          <div className="stats-grid fade-in">
            <div className="stat-card">
              <div className="stat-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#0B1F3A"/>
                  <text x="12" y="16" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">F</text>
                </svg>
              </div>
              <div className="stat-value">${totalBalance.toLocaleString()}</div>
              <div className="stat-label">Total Balance</div>
              <div className={`stat-change ${totalBalance >= 0 ? 'positive' : 'negative'}`}>
                <i className={`fas fa-arrow-${totalBalance >= 0 ? 'up' : 'down'}`}></i>
                {Math.abs(savingsRate)}% from last month
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <div className="stat-value">${profile?.monthly_income?.toLocaleString() || 0}</div>
              <div className="stat-label">Monthly Income</div>
              <div className="stat-change positive">
                <i className="fas fa-arrow-up"></i>
                Stable income
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-shopping-cart"></i>
              </div>
              <div className="stat-value">${calculateTotalSpent().toLocaleString() || 0}</div>
              <div className="stat-label">Monthly Spending</div>
              <div className={`stat-change ${calculateTotalSpent() < profile?.monthly_income ? 'positive' : 'negative'}`}>
                <i className={`fas fa-arrow-${calculateTotalSpent() < profile?.monthly_income ? 'down' : 'up'}`}></i>
                {Math.abs(savingsRate)}% of income
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-receipt"></i>
              </div>
              <div className="stat-value">{transactions?.length || 0}</div>
              <div className="stat-label">Transactions</div>
              <div className="stat-change positive">
                <i className="fas fa-arrow-up"></i>
                This month
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-credit-card"></i>
              </div>
              <div className="stat-value">${totalSubscriptions?.toLocaleString() || 0}</div>
              <div className="stat-label">Monthly Subscriptions</div>
              <div className="stat-change negative">
                <i className="fas fa-arrow-up"></i>
                Recurring expenses
              </div>
            </div>
          </div>

          {/* Charts and Tables Section */}
          <div className="row">
            {/* Recent Transactions */}
            <div className="col-lg-8">
              <Card className="fade-in">
                <Card.Header className="card-header">
                  <h5 className="card-title">Recent Transactions</h5>
                  <p className="card-subtitle">Your latest financial activities</p>
                </Card.Header>
                <Card.Body>
                  <Table striped hover responsive>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Category</th>
                        <th>Amount</th>
                        <th>Payment Mode</th>
                        <th>Type</th>
                        <th>Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions?.map((transaction, index) => (
                        <TransactionRow
                          key={index}
                          sNo={index + 1}
                          name={transaction.name}
                          category={transaction.category}
                          amount={transaction.amount}
                          paymentMode={transaction.paymentMode}
                          type={transaction.type}
                          date={transaction.date}
                          deleteTrans={deleteTrans}
                          _id={transaction.id}
                        />
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </div>
            
            {/* Category Breakdown */}
            <div className="col-lg-4">
              <Card className="fade-in">
                <Card.Header className="card-header">
                  <h5 className="card-title">Spending by Category</h5>
                  <p className="card-subtitle">Where your money goes</p>
                </Card.Header>
                <Card.Body>
                  <div className="ct-chart" id="spending-chart">
                    {(() => {
                      const categories = getCategories();
                      console.log('Categories for chart:', categories);
                      console.log('Categories length:', categories?.length);
                      
                      if (!categories || categories.length === 0) {
                        return (
                          <div className="text-center py-5">
                            <p>No transaction data available</p>
                          </div>
                        );
                      }
                      
                      const colors = ['#DC2626', '#F59E0B', '#FBBF24', '#10B981', '#3B82F6'];
                      const maxAmount = Math.max(...categories.map(cat => cat.totalAmount));
                      
                      return (
                        <div style={{ height: '300px', width: '100%', padding: '20px' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-end', height: '100%', gap: '10px' }}>
                            {categories.map((category, index) => {
                              const heightPercent = (category.totalAmount / maxAmount) * 100;
                              const color = colors[index % colors.length];
                              
                              return (
                                <div 
                                  key={index}
                                  style={{
                                    flex: 1,
                                    backgroundColor: color,
                                    height: `${heightPercent}%`,
                                    borderRadius: '4px',
                                    position: 'relative',
                                    minHeight: '20px'
                                  }}
                                  title={`${category.name}: $${category.totalAmount.toFixed(2)}`}
                                >
                                  <div 
                                    style={{
                                      position: 'absolute',
                                      top: '-25px',
                                      left: '50%',
                                      transform: 'translateX(-50%)',
                                      fontSize: '11px',
                                      fontWeight: 'bold',
                                      color: 'black'
                                    }}
                                  >
                                    {category.percentage}%
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="legend">
                    {(getCategories() || []).map((category, index) => (
                      <div key={index} className="legend-item" style={{display: 'flex', alignItems: 'center', marginBottom: '8px'}}>
                        <i 
                          className="fas fa-circle" 
                          style={{
                            color: category.color || '#3B82F6',
                            marginRight: '8px',
                            fontSize: '12px'
                          }}
                        ></i>
                        <span style={{color: category.name === 'General' ? '#3B82F6' : '#333', fontWeight: '500'}}>
                          {category.name}: ${category.totalAmount.toFixed(2)} ({category.percentage}%)
                        </span>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>

              {/* Goals Progress */}
              <Card className="fade-in mt-4">
                <Card.Header className="card-header">
                  <h5 className="card-title">Financial Goals</h5>
                  <p className="card-subtitle">Track your progress</p>
                </Card.Header>
                <Card.Body>
                  {goals?.map((goal, index) => (
                    <div key={index} className="goal-item mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">{goal.name}</h6>
                        <span className={`badge ${goal.completed ? 'bg-success' : 'bg-primary'}`}>
                          {goal.completed ? 'Completed' : 'In Progress'}
                        </span>
                      </div>
                      <div className="progress mb-2" style={{height: '8px'}}>
                        <div 
                          className="progress-bar bg-primary" 
                          style={{width: `${Math.min((goal.current_amount / goal.target_amount) * 100, 100)}%`}}
                        ></div>
                      </div>
                      <div className="d-flex justify-content-between">
                        <small className="text-muted">
                          ${goal.current_amount?.toFixed(2) || 0} of ${goal.target_amount?.toFixed(2) || 0}
                        </small>
                        <small className="text-muted">
                          {Math.round((goal.current_amount / goal.target_amount) * 100) || 0}% complete
                        </small>
                      </div>
                      {goal.description && (
                        <p className="text-muted small mb-0 mt-2">{goal.description}</p>
                      )}
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </div>
          </div>

          {/* Monthly Cycle */}
          <div className="row mt-4">
            <div className="col-12">
              <Card className="fade-in">
                <Card.Header className="card-header">
                  <h5 className="card-title">Monthly Cycle</h5>
                  <p className="card-subtitle">Days until next income</p>
                </Card.Header>
                <Card.Body>
                  <div className="cycle-info">
                    <div className="cycle-item">
                      <i className="fas fa-calendar-alt"></i>
                      <span>Next Payday: {moment(profile?.salary_date).add(1, 'months').format('MMMM DD, YYYY')}</span>
                    </div>
                    <div className="cycle-item">
                      <i className="fas fa-clock"></i>
                      <span>Days Remaining: {moment(profile?.salary_date).add(1, 'months').diff(moment(), 'days')}</span>
                    </div>
                  </div>
                  
                  {/* Monthly Cycle Progress Bar */}
                  <div className="cycle-progress mt-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Monthly Progress</span>
                      <span className="text-muted">{Math.round((30 - moment(profile?.salary_date).add(1, 'months').diff(moment(), 'days')) / 30 * 100)}%</span>
                    </div>
                    <div className="progress" style={{height: '10px'}}>
                      <div 
                        className="progress-bar" 
                        style={{
                          width: `${Math.round((30 - moment(profile?.salary_date).add(1, 'months').diff(moment(), 'days')) / 30 * 100)}%`,
                          backgroundColor: '#0B1F3A'
                        }}
                      ></div>
                    </div>
                    <small className="text-muted mt-1 d-block">
                      {30 - moment(profile?.salary_date).add(1, 'months').diff(moment(), 'days')} days passed of 30 days
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </div>
          </div>
        </Container>
      </div>
    </>
  );
}

export default PremiumDashboard;
